import { useTranslation } from 'react-i18next';

import { Caption } from '@/components/atoms/Eyebrow';
import SkeletonBox from '@/components/atoms/SkeletonBox/SkeletonBox';
import { formatPercentageByLocale, parseLocale } from '@/utils/localeFormatters';

import HealthWoundDistributionLegendIcon, {
    type WoundDistributionLegendIconVariant,
} from '../HealthWoundDistributionLegendIcon/HealthWoundDistributionLegendIcon';

import styles from './HealthWoundDistributionCard.module.scss';

export interface HealthWoundDistributionCardProps {
    isLoading?: boolean;
    noWoundPercent?: number | null;
    oneWoundPercent?: number | null;
    moreThanOneWoundPercent?: number | null;
}

interface DistributionLegendItemProps {
    iconVariant: WoundDistributionLegendIconVariant;
    label: string;
    value: string;
    isLoading?: boolean;
    showDivider?: boolean;
}

function DistributionLegendItem(props: DistributionLegendItemProps) {
    const { iconVariant, label, value, isLoading = false, showDivider = false } = props;

    return (
        <div className={showDivider ? styles.legendItemWithDivider : styles.legendItem}>
            <HealthWoundDistributionLegendIcon variant={iconVariant} />
            <div className={styles.legendText}>
                <Caption as="div">{label}</Caption>
                <div className={styles.legendValue}>
                    {isLoading ? (
                        <SkeletonBox
                            width={58}
                            height={20}
                            borderRadius={8}
                            className={styles.skeleton}
                        />
                    ) : (
                        <span>{value}</span>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function HealthWoundDistributionCard(props: HealthWoundDistributionCardProps) {
    const { isLoading = false, noWoundPercent, oneWoundPercent, moreThanOneWoundPercent } = props;
    const { t, i18n } = useTranslation('healthModule');
    const locale = parseLocale(i18n.language);

    return (
        <div className={styles.distributionCardContainer}>
            <div className={styles.legendContainer}>
                <DistributionLegendItem
                    iconVariant="noWound"
                    label={t('wounds.distributionNoWound')}
                    value={formatPercentageByLocale(noWoundPercent, locale)}
                    isLoading={isLoading}
                    showDivider
                />
                <DistributionLegendItem
                    iconVariant="oneWound"
                    label={t('wounds.distributionOneWound')}
                    value={formatPercentageByLocale(oneWoundPercent, locale)}
                    isLoading={isLoading}
                    showDivider
                />
                <DistributionLegendItem
                    iconVariant="multipleWounds"
                    label={t('wounds.distributionMultipleWounds')}
                    value={formatPercentageByLocale(moreThanOneWoundPercent, locale)}
                    isLoading={isLoading}
                />
            </div>
        </div>
    );
}
